import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { doc, updateDoc, deleteDoc, getDocs, getDoc, collection } from 'firebase/firestore';
import { LayoutList, Search, Trophy, ChevronRight, ChevronDown, X } from 'lucide-react';
import { Tournament, Team, Match, Club, Config, Player, UserProfile, RankingConfig } from '../../types';
import { AutoTournamentWizard } from './AutoTournamentWizard';
import { TournamentWizard } from './TournamentWizard';
import { AdminTournamentCard } from './AdminTournamentCard';
import { AdminArchivedTournamentRow } from './AdminArchivedTournamentRow';
import { PublicTournamentCard } from '../tournament/PublicTournamentCard';
import { AdminPodiumEditModal } from './AdminPodiumEditModal';

interface AdminTorneosProps {
  tournaments: Tournament[];
  allTeams: Team[];
  players: Player[];
  matches: Match[];
  clubs: Club[];
  db: any;
  getCollectionPath: (col: string) => string;
  config: Config;
  rankingConfig: RankingConfig;
  handleShareCanvas: (canvas: HTMLCanvasElement, filename: string, action: 'download' | 'whatsapp') => void;
  onPlayerClick: (id: string | null) => void;
  organizerId: string;
  userRole: 'user' | 'admin' | 'superadmin' | 'co_admin' | 'empleado' | 'gestor_turnos' | 'gestor_torneos';
  sedesPublicas?: UserProfile[];
  isOrganizerMode?: boolean;
}

export function AdminTorneos({
  tournaments,
  allTeams,
  players,
  matches,
  clubs,
  db,
  getCollectionPath,
  config,
  rankingConfig,
  handleShareCanvas,
  onPlayerClick,
  organizerId,
  userRole,
  sedesPublicas = [],
}: AdminTorneosProps) {
  const [showWizard, setShowWizard] = useState(false);
  const [showAutoWizard, setShowAutoWizard] = useState(false);
  const [adminTorneosTab, setAdminTorneosTab] = useState('inscripciones');
  const [searchAdminTournaments, setSearchAdminTournaments] = useState('');
  const [adminCategoryFilter, setAdminCategoryFilter] = useState('');
  const [expandedTournamentId, setExpandedTournamentId] = useState<string | null>(null);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);
  const [archivedTournamentModalId, setArchivedTournamentModalId] = useState<string | null>(null);
  const [editingChampionTId, setEditingChampionTId] = useState<string | null>(null);

  const handleDeleteTournament = async (t: Tournament) => {
    if (window.confirm(`¿Estás seguro que deseas eliminar el torneo "${t.name}"? Esta acción no se puede deshacer.`)) {
      try {
        await deleteDoc(doc(db, getCollectionPath('tournaments'), t.id));
        
        if (t.locationClubIds && Array.isArray(t.locationClubIds)) {
          for (const clubId of t.locationClubIds) {
            try {
              const scheduleRef = doc(db, getCollectionPath('clubSchedules'), clubId);
              const snap = await getDoc(scheduleRef);
              if (snap.exists()) {
                const data = snap.data();
                if (data.offDays) {
                  const filteredOffDays = data.offDays.filter((od: any) => !od.id.startsWith(`block_${t.id}_`));
                  if (filteredOffDays.length !== data.offDays.length) {
                    await updateDoc(scheduleRef, { offDays: filteredOffDays });
                  }
                }
              }
            } catch (e) {
              console.error("Error eliminando bloqueos de club:", e);
            }
          }
        }
      } catch (e) {
        console.error("Error deleting tournament:", e);
        alert("Error al eliminar el torneo");
      }
    }
  };

  useEffect(() => {
    const handleOpenAdminT = (e: any) => {
      const targetId = e.detail;
      if (!targetId) return;

      const targetT = tournaments.find(t => t.id === targetId);
      if (targetT) {
        if (targetT.isArchived || targetT.status === 'Finalizado') {
          setAdminTorneosTab('historial');
        } else if (targetT.status === 'En Juego' || targetT.status === 'Fase de Grupos' || targetT.status === 'Eliminatorias') {
          setAdminTorneosTab('activos');
        } else {
          setAdminTorneosTab('inscripciones');
        }
      }

      setExpandedTournamentId(targetId);
      setSelectedTournamentId(targetId);

      setTimeout(() => {
        const el = document.querySelector(`[data-tournament-id="${targetId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('nav-highlight');
          setTimeout(() => el.classList.remove('nav-highlight'), 2500);
        }
      }, 500);
    };

    const handleTabChange = (e: any) => {
      if (e.detail.tab === 'resultados' || e.detail.tab === 'marca') return; // AdminTorneos no maneja estas tabs
      setAdminTorneosTab(e.detail.tab);
      setArchivedTournamentModalId(null);
      if (e.detail.tournamentId) {
        // Small delay to allow Firestore state to catch up
        setTimeout(() => {
          setExpandedTournamentId(e.detail.tournamentId);
          setSelectedTournamentId(e.detail.tournamentId);

          setTimeout(() => {
            const el = document.querySelector(`[data-tournament-id="${e.detail.tournamentId}"]`);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.classList.add('nav-highlight');
              setTimeout(() => el.classList.remove('nav-highlight'), 2500);
            }
          }, 350);
        }, 150);
      }
    };
    window.addEventListener('openAdminTournament', handleOpenAdminT);
    window.addEventListener('changeAdminTab', handleTabChange);
    return () => {
      window.removeEventListener('openAdminTournament', handleOpenAdminT);
      window.removeEventListener('changeAdminTab', handleTabChange);
    };
  }, [tournaments]);

  const toggleTournament = (id: string) => {
    if (expandedTournamentId === id) {
      setExpandedTournamentId(null);
      setSelectedTournamentId(null);
    } else {
      setExpandedTournamentId(id);
      setSelectedTournamentId(id);
    }
  };

  // Tab counts
  const inscripcionesCount = tournaments.filter(t => !t.isArchived && (t.status === 'Registro' || t.status === 'Borrador' || t.status === 'Lobby' || !t.status)).length;
  const activosCount = tournaments.filter(t => !t.isArchived && (t.status === 'En Juego' || t.status === 'Fase de Grupos' || t.status === 'Eliminatorias')).length;

  useEffect(() => {
    if (!searchAdminTournaments) {
      return;
    }

    const query = searchAdminTournaments.toLowerCase();
    const queryMatches = tournaments.filter(t => {
      const nameMatch = (t.name || '').toLowerCase().includes(query);
      const categoryMatch = !adminCategoryFilter || t.category === adminCategoryFilter;
      if (!nameMatch || !categoryMatch) return false;

      if (adminTorneosTab === 'inscripciones') {
        return !t.isArchived && (t.status === 'Registro' || t.status === 'Borrador' || t.status === 'Lobby' || !t.status);
      }
      if (adminTorneosTab === 'activos') {
        return !t.isArchived && (t.status === 'En Juego' || t.status === 'Fase de Grupos' || t.status === 'Eliminatorias');
      }
      if (adminTorneosTab === 'historial') {
        return t.isArchived || t.status === 'Finalizado';
      }
      return false;
    });

    if (queryMatches.length > 0) {
      setExpandedTournamentId(queryMatches[0].id);
      setSelectedTournamentId(queryMatches[0].id);
    }
  }, [searchAdminTournaments, adminTorneosTab]);

  return (
    <div className="space-y-10">
      <div className="flex flex-col lg:flex-row justify-center gap-4 md:gap-6 mb-8 items-center">
        <div className="flex flex-col sm:flex-row items-center justify-center">
          <div className="flex gap-2 md:gap-3 w-full sm:w-auto justify-center">
            {[
              { id: 'inscripciones', label: 'Inscripciones', count: inscripcionesCount },
              { id: 'activos', label: 'Torneos Activos', count: activosCount },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setAdminTorneosTab(tab.id);
                  // Do not reset expansion here - let the tab handle what is expanded
                }}
                className={`flex items-center px-4 md:px-5 py-2 md:py-2.5 rounded-full text-[11px] md:text-[13px] font-bold transition-all uppercase tracking-widest ${adminTorneosTab === tab.id
                    ? 'bg-white/10 text-white shadow-sm'
                    : 'text-white/40 hover:text-white hover:bg-white/5'
                  }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-black ${
                    adminTorneosTab === tab.id ? 'theme-primary-bg text-black shadow-lg shadow-yellow-500/20' : 'bg-white/10 text-white/60'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}

            <button
              onClick={() => {
                setAdminTorneosTab('historial');
                setExpandedTournamentId(null);
                setSelectedTournamentId(null);
              }}
              className={`px-4 md:px-5 py-2 md:py-2.5 rounded-full text-[11px] md:text-[13px] font-bold transition-all uppercase tracking-widest flex items-center justify-center ${adminTorneosTab === 'historial'
                  ? 'bg-white/10 text-white shadow-sm'
                  : 'text-white/40 hover:text-white hover:bg-white/5'
                }`}
            >
              Historial
            </button>
          </div>
        </div>

        {!showWizard && !showAutoWizard && (
          <div className="flex gap-2 w-full sm:w-auto mt-4 lg:mt-0">
            <button
              onClick={() => setShowWizard(true)}
              className="theme-primary-bg hover:bg-yellow-400 text-black font-black px-4 md:px-6 py-2.5 md:py-2.5 rounded-xl uppercase tracking-widest transition-all flex-1 md:flex-none shadow-lg shadow-yellow-500/20 text-[11px] md:text-[13px] flex items-center justify-center gap-2 hover:scale-105"
            >
              <span className="text-lg md:text-xl leading-none font-black">+</span>
              Nuevo Torneo
            </button>
          </div>
        )}
      </div>

      {showWizard && (
        <TournamentWizard
          db={db}
          getCollectionPath={getCollectionPath}
          onCancel={() => setShowWizard(false)}
          organizerId={organizerId}
          players={players}
          userRole={userRole}
          sedesPublicas={sedesPublicas}
        />
      )}

      {showAutoWizard && (
        <AutoTournamentWizard
          allTeams={allTeams}
          db={db}
          getCollectionPath={getCollectionPath}
          onCancel={() => setShowAutoWizard(false)}
          organizerId={organizerId}
          players={players}
          userRole={userRole}
        />
      )}

      {(() => {
        const filtered = tournaments
          .filter((t) => {
            const name = (t.name || '').toLowerCase();
            const query = searchAdminTournaments.toLowerCase();
            const matchesSearch = name.includes(query);
            const matchesCategory = !adminCategoryFilter || t.category === adminCategoryFilter;

            if (!matchesCategory) return false;

            if (adminTorneosTab === 'inscripciones') {
              return matchesSearch && !t.isArchived && (t.status === 'Registro' || t.status === 'Borrador' || t.status === 'Lobby' || !t.status);
            }
            if (adminTorneosTab === 'activos') {
              return matchesSearch && !t.isArchived && (t.status === 'En Juego' || t.status === 'Fase de Grupos' || t.status === 'Eliminatorias');
            }
            if (adminTorneosTab === 'historial') {
              return matchesSearch && (t.isArchived || t.status === 'Finalizado');
            }
            return false;
          })
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

        if (filtered.length === 0 && tournaments.length > 0) {
          return (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-center border border-dashed border-white/10 rounded-3xl mt-8">
              <Trophy size={48} className="text-white/10 mb-4" />
              <p className="text-white/40 text-sm font-medium tracking-wide">
                {searchAdminTournaments
                  ? `Sin resultados para "${searchAdminTournaments}"`
                  : `No hay torneos en ${adminTorneosTab} en este momento.`}
              </p>
            </div>
          );
        }

        if (adminTorneosTab === 'historial') {
          return (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 bg-black/20 border border-white/10 rounded-xl px-3 py-2 max-w-md mx-auto w-full">
                <Search size={16} className="text-white/40" />
                <input
                  type="text"
                  placeholder="Buscar torneo finalizado..."
                  value={searchAdminTournaments}
                  onChange={(e) => setSearchAdminTournaments(e.target.value)}
                  className="bg-transparent border-none outline-none text-sm text-white w-full placeholder:text-white/30"
                />
                {searchAdminTournaments && (
                  <button onClick={() => setSearchAdminTournaments('')} className="text-white/40 hover:text-white">
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-3">
                {filtered.map((t) => (
                  <AdminArchivedTournamentRow 
                    key={t.id}
                    t={t}
                    config={config}
                    allTeams={allTeams}
                    players={players}
                    sedesPublicas={sedesPublicas}
                    onClick={() => setArchivedTournamentModalId(t.id)}
                    onEditChampion={userRole === 'superadmin' ? () => setEditingChampionTId(t.id) : undefined}
                    onDelete={userRole === 'superadmin' ? () => handleDeleteTournament(t) : undefined}
                  />
                ))}
              </div>
              {archivedTournamentModalId && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/40 backdrop-blur-sm">
                  <div className="bg-[#0f172a] w-full max-w-5xl max-h-[95vh] overflow-y-auto rounded-3xl relative border border-white/10 shadow-2xl custom-scrollbar animate-in zoom-in-95 duration-300">
                    <button
                      onClick={() => setArchivedTournamentModalId(null)}
                      className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-white/10 text-white/50 hover:text-white rounded-xl transition-all border border-white/10 shadow-lg"
                    >
                      <X size={20} />
                    </button>
                    <div className="p-2 sm:p-4">
                      {(() => {
                        const tModal = filtered.find(t => t.id === archivedTournamentModalId);
                        if (!tModal) return null;
                        return (
                          <PublicTournamentCard
                            index={0}
                            tournament={tModal}
                            allTeams={allTeams}
                            players={players}
                            clubs={clubs}
                            config={config}
                            rankingConfig={rankingConfig}
                            handleShareCanvas={handleShareCanvas}
                            onPlayerClick={onPlayerClick}
                            onJoin={() => {}}
                            onCancelJoin={() => {}}
                            onOpenChat={() => {}}
                            isExpanded={true}
                            onToggle={() => {}}
                            sedesPublicas={sedesPublicas}
                          />
                        );
                      })()}
                    </div>
                  </div>
                </div>,
                document.body
              )}
            </div>
          );
        }

        return filtered.map((t, index) => (
          <AdminTournamentCard
            key={t.id}
            index={index}
            t={t}
            allTeams={allTeams}
            players={players}
            matches={matches}
            clubs={clubs}
            db={db}
            getCollectionPath={getCollectionPath}
            config={config}
            rankingConfig={rankingConfig}
            handleShareCanvas={handleShareCanvas}
            onPlayerClick={onPlayerClick}
            isExpanded={expandedTournamentId === t.id}
            onToggle={() => toggleTournament(t.id)}
            isSelected={selectedTournamentId === t.id}
            sedesPublicas={sedesPublicas}
          />
        ));
      })()}

      {editingChampionTId && (() => {
        const tToEdit = tournaments.find(t => t.id === editingChampionTId);
        if (!tToEdit) return null;
        return (
          <AdminPodiumEditModal
            t={tToEdit}
            allTeams={allTeams}
            players={players}
            rankingConfig={rankingConfig}
            db={db}
            getCollectionPath={getCollectionPath}
            onClose={() => setEditingChampionTId(null)}
          />
        );
      })()}
    </div>
  );
}
